import logger from './logger';

export class ABICacheManager {
    private static readonly CACHE_VERSION = '1.0.0';
    private static readonly CACHE_VERSION_KEY = 'abi-cache-version';

    /**
     * Clear all ABI cache entries from localStorage
     */
    static clearAllABICache(): void {
        const isDev = import.meta.env.DEV;

        if (isDev) {
            logger.log('🧹 Clearing all ABI cache entries...');
        }

        const keysToRemove: string[] = [];

        // Find all localStorage keys that contain ABI cache data
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('abi-') || key.includes('ABI'))) {
                keysToRemove.push(key);
            }
        }

        // Remove all ABI cache keys (no logging per key in production)
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            if (isDev) {
                logger.log(`Removed cache key: ${key}`);
            }
        });

        // Set new cache version
        localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);

        // Always show summary
        logger.log(`✅ Cleared ${keysToRemove.length} ABI cache entries`);
    }

    /**
     * Clear cache for a specific contract ABI
     */
    static clearContractABICache(chainId: string, contractName: string): void {
        const cacheKey = `abi-${chainId}-${contractName}`;
        localStorage.removeItem(cacheKey);
        logger.log(`Cleared cache for ${contractName} on chain ${chainId}`);
    }

    /**
     * Check if cache version is current, clear if outdated
     */
    static validateCacheVersion(): boolean {
        const currentVersion = localStorage.getItem(this.CACHE_VERSION_KEY);

        if (currentVersion !== this.CACHE_VERSION) {
            logger.log(`🔄 Cache version mismatch. Current: ${currentVersion}, Expected: ${this.CACHE_VERSION}`);
            this.clearAllABICache();
            return false;
        }

        return true;
    }

    /**
     * Get cache key for a contract ABI
     */
    static getCacheKey(chainId: string, contractName: string): string {
        return `abi-${chainId}-${contractName}`;
    }

    /**
     * Validate that an ABI has expected functions
     */
    static validateABICompleteness(abi: any[], contractName: string, expectedFunctions?: string[]): boolean {
        const isDev = import.meta.env.DEV;

        if (!Array.isArray(abi) || abi.length === 0) {
            logger.warn(`❌ ABI validation failed: Empty or invalid ABI for ${contractName}`);
            return false;
        }

        const functions = abi.filter(item => item.type === 'function');

        // Only log validation details in development
        if (isDev) {
            logger.log(`📊 ABI validation for ${contractName}: ${functions.length} functions found`);
        }

        // Specific validation for LPTokenLocker
        if (contractName === 'LPTokenLocker') {
            const requiredFunctions = [
                'ASSET_RECOVERY_ROLE',
                'getLockedAsset',
                'getLockedAssetsCount',
                'getOriginalAmount',
                'lockAsset',
                'withdrawAsset'
            ];

            const missingFunctions = requiredFunctions.filter(funcName =>
                !functions.some(f => f.name === funcName)
            );

            if (missingFunctions.length > 0) {
                logger.warn(`❌ LPTokenLocker ABI missing functions: ${missingFunctions.join(', ')}`);
                return false;
            }

            // Expect at least 30 functions for LPTokenLocker
            if (functions.length < 30) {
                logger.warn(`❌ LPTokenLocker ABI incomplete: only ${functions.length} functions (expected 30+)`);
                return false;
            }
        }

        // Check for expected functions if provided
        if (expectedFunctions && expectedFunctions.length > 0) {
            const missingFunctions = expectedFunctions.filter(funcName =>
                !functions.some(f => f.name === funcName)
            );

            if (missingFunctions.length > 0) {
                logger.warn(`❌ ABI missing expected functions: ${missingFunctions.join(', ')}`);
                return false;
            }
        }

        // Only log success in development
        if (isDev) {
            logger.log(`✅ ABI validation passed for ${contractName}`);
        }
        return true;
    }

    /**
     * Force refresh of a specific contract's ABI
     */
    static async forceRefreshContractABI(chainId: string, contractName: string): Promise<void> {
        // Clear the cache for this specific contract
        this.clearContractABICache(chainId, contractName);

        // Force a page reload to ensure fresh ABI loading
        logger.log(`🔄 Forcing refresh for ${contractName} ABI...`);

        // You could also dispatch a custom event here to trigger ABI reload
        window.dispatchEvent(new CustomEvent('abi-cache-cleared', {
            detail: { chainId, contractName }
        }));
    }

    /**
     * Add cache busting parameter to ABI requests
     */
    static addCacheBuster(url: string): string {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}v=${Date.now()}`;
    }
}

// Initialize cache validation on module load
ABICacheManager.validateCacheVersion();
